import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { BlockedUserSummary, MatchedSignal } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';

import { SIGNAL_TYPE_LABELS } from '../constants';
import { groupSignals } from '../signalGrouping';
import BlockedUserLink from './BlockedUserLink';

interface Props {
  signals: MatchedSignal[];
  summaries: BlockedUserSummary[];
}

function SignalDetailsPanel({ signals, summaries }: Props) {
  const grouped = groupSignals(signals);
  const summaryById = new Map(summaries.map((s) => [s.id, s]));

  return (
    <Box sx={{ py: 1 }}>
      {Object.entries(grouped).map(([type, valueGroups]) => (
        <Box key={type} sx={{ mb: 1.5 }}>
          <Typography
            variant='caption'
            sx={{
              fontWeight: 600,
              color: colors.gray[500],
              display: 'block',
              mb: 0.5,
            }}
          >
            {SIGNAL_TYPE_LABELS[type] || type} (weight: {valueGroups[0]?.weight ?? 0})
          </Typography>
          {valueGroups.map((group) => (
            <Box
              key={group.value}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                ml: 1,
                mb: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant='body2'
                title={group.value}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  maxWidth: '60%',
                }}
              >
                {group.value}
              </Typography>
              <Typography
                variant='caption'
                sx={{ color: colors.gray[500], pt: 0.25 }}
              >
                →
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {group.blockedUserIds.map((id, idx) => (
                  <React.Fragment key={id}>
                    <BlockedUserLink id={id} summary={summaryById.get(id)} />
                    {idx < group.blockedUserIds.length - 1 && (
                      <Typography variant='caption' sx={{ color: colors.gray[500] }}>
                        ,
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default SignalDetailsPanel;
